import { createSlice } from "@reduxjs/toolkit";
import { USER_ROLES } from "../../app.constants";
import type { RootState } from "../../app.store";
import {
  ORGANIZATION_SESSION_ERRORS,
  type AuthInitialState,
  type TOrganizationSessionPayload,
  type UserInfo,
  type VerifyPayload,
} from "./auth.types";

/** localStorage key for the selected organization session (MULTI_ORG logins). */
export const ORG_SESSION_STORAGE_KEY = "org_session";

/** Read the stored organization session. Returns null if missing or corrupted. */
export function readStoredOrgSession(): TOrganizationSessionPayload | null {
  try {
    const raw = localStorage.getItem(ORG_SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeStoredOrgSession(session: TOrganizationSessionPayload) {
  localStorage.setItem(ORG_SESSION_STORAGE_KEY, JSON.stringify(session));
}

/** True when an API error means the organization session must be picked again. */
export function isOrganizationSessionError(error: unknown): boolean {
  const code = (error as any)?.data?.message?.code;
  return (
    (error as any)?.status === 403 &&
    (ORGANIZATION_SESSION_ERRORS as readonly string[]).includes(code)
  );
}

const initialState: AuthInitialState = {
  theme: "dark",
  role: undefined,
  modulesInfo: [],
  permissionsInfo: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    verifyUser: (state, { payload }: { payload: VerifyPayload }) => {
      state.verifiedInfo = payload?.user;
      state.access_token = payload?.access_token;
      state.userId = payload?.user?.id;
    },
    userLoggedOut: (state) => {
      state.userId = undefined;
      state.userInfo = undefined;
      state.profileInfo = undefined;
      state.access_token = undefined;
      state.verifiedInfo = undefined;
      state.permissionsInfo = [];
      state.modulesInfo = [];
      state.sessionId = undefined;
      state.sessionUserId = undefined;
      state.selectedOrganization = undefined;

      // Clear localStorage
      localStorage.clear();
    },
    setOrganizationSession: (
      state,
      { payload }: { payload: TOrganizationSessionPayload },
    ) => {
      if (payload.sessionId) state.sessionId = payload.sessionId;
      if (payload.organization) state.selectedOrganization = payload.organization;
      if (payload.userId) {
        state.sessionUserId = payload.userId;
        state.userId = payload.userId;
      }

      writeStoredOrgSession({
        sessionId: state.sessionId,
        userId: state.sessionUserId,
        organization: state.selectedOrganization,
      });
    },
    clearOrganizationSession: (state) => {
      state.sessionId = undefined;
      state.sessionUserId = undefined;
      state.selectedOrganization = undefined;
      state.userId = state.verifiedInfo?.id;
      state.profileInfo = undefined;
      state.permissionsInfo = [];
      state.modulesInfo = [];
      state.role = undefined;
      localStorage.removeItem(ORG_SESSION_STORAGE_KEY);
    },
    setAuthInfo: (state, { payload }: { payload: UserInfo }) => {
      state.userInfo = payload;
      state.userId = payload?.userId;
    },
    setProfileInfo: (state, { payload }) => {
      state.profileInfo = payload;
      if (state.permissionsInfo.length) return;
      if (
        payload.role !== "admin" &&
        payload?.is_evaluator &&
        payload?.is_manager
      ) {
        state.role = USER_ROLES.EVALUATOR_MANAGER;
        return;
      } else if (
        payload.role !== "admin" &&
        payload?.is_evaluator &&
        !payload?.is_manager
      ) {
        state.role = USER_ROLES.EVALUATOR_USER;
        return;
      }
      if (payload.role !== "admin" && payload?.is_evaluator) {
        state.role = USER_ROLES.EVALUATOR;
        return;
      }
      if (payload.role !== "admin" && payload?.is_manager) {
        state.role = USER_ROLES.LINE_MANAGER;
        return;
      } else {
        state.role = USER_ROLES.USER;
      }
      // toast("error", "Insufficient permissions.");
    },
    setModulesInfo: (state, { payload }) => {
      state.modulesInfo = payload;
    },
    setPermissionsInfo: (state, { payload }) => {
      if (state.role !== USER_ROLES.ADMIN && payload?.length > 0) {
        state.role = USER_ROLES.ADMIN;
      }
      state.permissionsInfo = payload;
    },
    setTheme: (state, action) => {
      state.theme = action.payload;
      if (typeof window !== "undefined") {
        localStorage.setItem("theme", action.payload);
      }
    },
    toggleTheme: (state) => {
      state.theme = state.theme === "light" ? "dark" : "light";
      // if (typeof window !== 'undefined') {
      //   localStorage.setItem('theme', state.theme);
      // }
    },
  },
});

export const {
  userLoggedOut,
  setAuthInfo,
  toggleTheme,
  verifyUser,
  setTheme,
  setProfileInfo,
  setModulesInfo,
  setPermissionsInfo,
  setOrganizationSession,
  clearOrganizationSession,
} = authSlice.actions;
export const authReducer = authSlice.reducer;

/* ------ Selectors ------ */
export const selectAuth = (state: RootState) => state.auth;
